import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  WarningRegular,
  AddRegular,
  LinkRegular,
  BuildingRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import { useTranslation, Trans } from 'react-i18next';
import {
  statusDisplayNames,
  statusColors,
  appointmentTypeDisplayNames,
  appointmentTypeColors,
  type TaxReturn,
  type AppointmentType,
} from '../../types';
import StatusTimeline from '../../components/StatusTimeline';
import { getTransitionHint } from '../../utils/workflowTransitions';
import type { UseMutationResult } from '@tanstack/react-query';

export interface WorkflowTabProps {
  taxReturn: TaxReturn;
  potentialDuplicates?: TaxReturn[];
  linkedReturns?: TaxReturn[];
  appointmentReturns?: TaxReturn[];
  duplicateWarningDismissed: boolean;
  onDuplicateWarningDismiss: () => void;
  mergeMutation: UseMutationResult<TaxReturn, Error, string>;
  onAddReturnDialogOpen: () => void;
}

export function WorkflowTab({
  taxReturn,
  potentialDuplicates,
  linkedReturns,
  appointmentReturns,
  duplicateWarningDismissed,
  onDuplicateWarningDismiss,
  mergeMutation,
  onAddReturnDialogOpen,
}: WorkflowTabProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Duplicate Return Warning */}
      {potentialDuplicates && potentialDuplicates.length > 0 && !duplicateWarningDismissed && (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#fff4ce',
            borderLeft: '4px solid #f59e0b',
            borderRadius: '4px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <WarningRegular style={{ color: '#f59e0b', fontSize: tokens.fontSizeBase600, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', color: '#92400e' }}>
              {t('workflowTab.duplicateWarning.heading')}
            </div>
            <div style={{ fontSize: tokens.fontSizeBase300, color: '#78350f', marginBottom: '12px' }}>
              {t('workflowTab.duplicateWarning.clientHasReturns', {
                returns: potentialDuplicates.length === 1
                  ? t('workflowTab.duplicateWarning.anotherReturn')
                  : t('workflowTab.duplicateWarning.otherReturnsCount', { count: potentialDuplicates.length }),
                year: taxReturn.taxYear,
              })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {potentialDuplicates.map(dup => (
                <div
                  key={dup.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    borderRadius: '4px',
                    gap: '12px',
                  }}
                >
                  <Link
                    to={`/tax-returns/${dup.id}`}
                    style={{
                      flex: 1,
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <strong>{dup.clientName}</strong>
                    <span style={{ color: '#78350f', fontSize: '13px', marginLeft: '8px' }}>
                      {statusDisplayNames[dup.status]} • {t('workflowTab.duplicateWarning.appointmentLabel', { date: format(new Date(dup.scheduledDate), 'MMM d, yyyy') })}
                    </span>
                  </Link>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Badge
                      size="small"
                      style={{
                        backgroundColor: statusColors[dup.status],
                        color: 'white',
                      }}
                    >
                      {statusDisplayNames[dup.status]}
                    </Badge>
                    <Button
                      size="small"
                      appearance="primary"
                      icon={<LinkRegular />}
                      onClick={() => mergeMutation.mutate(dup.id)}
                      disabled={mergeMutation.isPending}
                    >
                      {t('workflowTab.duplicateWarning.mergeIntoThis')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                size="small"
                appearance="secondary"
                onClick={onDuplicateWarningDismiss}
              >
                {t('workflowTab.duplicateWarning.keepAsSeparate')}
              </Button>
              <span style={{ fontSize: tokens.fontSizeBase200, color: '#78350f', alignSelf: 'center' }}>
                {t('workflowTab.duplicateWarning.mergeHint')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Type Workflow Guidance */}
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: taxReturn.appointmentType === 'in-person' ? '#e6f2ff'
          : taxReturn.appointmentType === 'virtual' ? '#f3f0ff'
          : '#fff7ed',
        borderLeft: `4px solid ${appointmentTypeColors[taxReturn.appointmentType as AppointmentType] || '#6B7280'}`,
        borderRadius: '4px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Badge
            appearance="filled"
            style={{
              backgroundColor: appointmentTypeColors[taxReturn.appointmentType as AppointmentType] || '#6B7280',
              color: 'white',
            }}
          >
            {appointmentTypeDisplayNames[taxReturn.appointmentType as AppointmentType] || taxReturn.appointmentType}
          </Badge>
          <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{t('workflowTab.appointmentGuidance.workflowLabel')}</span>
        </div>
        <div style={{ fontSize: tokens.fontSizeBase300, color: '#374151' }}>
          {taxReturn.appointmentType === 'in-person' && (
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>{t('workflowTab.appointmentGuidance.inPerson.step1')}</li>
              <li>{t('workflowTab.appointmentGuidance.inPerson.step2')}</li>
              <li><Trans i18nKey="workflowTab.appointmentGuidance.inPerson.step3" components={{ strong: <strong /> }} /></li>
              <li>{t('workflowTab.appointmentGuidance.inPerson.step4')}</li>
            </ul>
          )}
          {taxReturn.appointmentType === 'virtual' && (
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><Trans i18nKey="workflowTab.appointmentGuidance.virtual.step1" components={{ strong: <strong /> }} /></li>
              <li>{t('workflowTab.appointmentGuidance.virtual.step2')}</li>
              <li><Trans i18nKey="workflowTab.appointmentGuidance.signatureViaZoho" components={{ strong: <strong /> }} /></li>
              <li>{t('workflowTab.appointmentGuidance.virtual.step4')}</li>
            </ul>
          )}
          {taxReturn.appointmentType === 'drop-off' && (
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>{t('workflowTab.appointmentGuidance.dropOff.step1')}</li>
              <li>{t('workflowTab.appointmentGuidance.dropOff.step2')}</li>
              <li><Trans i18nKey="workflowTab.appointmentGuidance.signatureViaZoho" components={{ strong: <strong /> }} /></li>
              <li>{t('workflowTab.appointmentGuidance.dropOff.step4')}</li>
            </ul>
          )}
        </div>
      </div>

      {getTransitionHint(taxReturn.status, taxReturn.appointmentType as AppointmentType) && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '4px',
          fontSize: tokens.fontSizeBase300,
          color: '#166534',
          fontWeight: 500,
        }}>
          {t('workflowTab.nextStepHint', { hint: getTransitionHint(taxReturn.status, taxReturn.appointmentType as AppointmentType) })}
        </div>
      )}

      {/* Status Timeline */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <span className="card-title">{t('workflowTab.progress.heading')}</span>
        </div>
        <div style={{ padding: '16px' }}>
          <StatusTimeline
            statusHistory={taxReturn.statusHistory || []}
            currentStatus={taxReturn.status}
            showAllStages
            orientation="vertical"
          />
        </div>
      </div>

      {/* Linked Returns */}
      {((linkedReturns && linkedReturns.length > 0) || taxReturn.parentReturnId) && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              <LinkRegular style={{ marginRight: '8px' }} />
              {t('workflowTab.linkedReturns.heading')}
            </span>
            <Button
              appearance="subtle"
              icon={<AddRegular />}
              onClick={onAddReturnDialogOpen}
            >
              {t('workflowTab.linkedReturns.addReturn')}
            </Button>
          </div>
          <div style={{ padding: '16px' }}>
            {taxReturn.parentReturnId && (
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', textTransform: 'uppercase' }}>{t('workflowTab.linkedReturns.parentReturnLabel')}</span>
                <Link
                  to={`/tax-returns/${taxReturn.parentReturnId}`}
                  style={{
                    display: 'block',
                    padding: '12px',
                    backgroundColor: 'var(--tl-bg-subtle)',
                    borderRadius: '4px',
                    marginTop: '4px',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  {t('workflowTab.linkedReturns.viewParentReturn')}
                </Link>
              </div>
            )}
            {linkedReturns && linkedReturns.length > 0 && (
              <div>
                <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', textTransform: 'uppercase' }}>{t('workflowTab.linkedReturns.linkedReturnsLabel')}</span>
                {linkedReturns.map(linked => (
                  <div
                    key={linked.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: 'var(--tl-bg-subtle)',
                      borderRadius: '4px',
                      marginTop: '8px',
                    }}
                  >
                    <Link
                      to={`/tax-returns/${linked.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        textDecoration: 'none',
                        color: 'inherit',
                        flex: 1,
                      }}
                    >
                      {linked.returnType === 'business' ? <BuildingRegular /> : <PersonRegular />}
                      <div>
                        <div style={{ fontWeight: 500 }}>{linked.clientName}</div>
                        <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                          {linked.returnType === 'business' ? t('workflowTab.common.business') : t('workflowTab.common.individual')} • {linked.taxYear}
                        </div>
                      </div>
                    </Link>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Badge
                        appearance="filled"
                        style={{
                          backgroundColor: statusColors[linked.status],
                          color: 'white',
                        }}
                      >
                        {statusDisplayNames[linked.status]}
                      </Badge>
                      {linked.returnType === 'business' && taxReturn.returnType === 'individual' && (
                        <Button
                          size="small"
                          appearance="primary"
                          icon={<LinkRegular />}
                          onClick={() => mergeMutation.mutate(linked.id)}
                          disabled={mergeMutation.isPending}
                        >
                          {t('workflowTab.duplicateWarning.mergeIntoThis')}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Appointment Returns (same appointment, not necessarily linked) */}
      {appointmentReturns && appointmentReturns.length > 0 && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <span className="card-title">
              <BuildingRegular style={{ marginRight: '8px' }} />
              {t('workflowTab.appointmentReturns.heading')}
            </span>
          </div>
          <div style={{ padding: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginBottom: '12px' }}>
              {t('workflowTab.appointmentReturns.description')}
            </div>
            {appointmentReturns.map(apptReturn => {
              // Don't show if already in linkedReturns
              const isAlreadyLinked = linkedReturns?.some(lr => lr.id === apptReturn.id);
              if (isAlreadyLinked) return null;
              return (
                <div
                  key={apptReturn.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: 'var(--tl-bg-subtle)',
                    borderRadius: '4px',
                    marginTop: '8px',
                  }}
                >
                  <Link
                    to={`/tax-returns/${apptReturn.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      textDecoration: 'none',
                      color: 'inherit',
                      flex: 1,
                    }}
                  >
                    {apptReturn.returnType === 'business' ? <BuildingRegular /> : <PersonRegular />}
                    <div>
                      <div style={{ fontWeight: 500 }}>{apptReturn.clientName}</div>
                      <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                        {apptReturn.returnType === 'business' ? t('workflowTab.common.business') : t('workflowTab.common.individual')} • {apptReturn.taxYear}
                      </div>
                    </div>
                  </Link>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Badge
                      appearance="filled"
                      style={{
                        backgroundColor: statusColors[apptReturn.status],
                        color: 'white',
                      }}
                    >
                      {statusDisplayNames[apptReturn.status]}
                    </Badge>
                    {apptReturn.returnType === 'business' && taxReturn.returnType === 'individual' && (
                      <Button
                        size="small"
                        appearance="primary"
                        icon={<LinkRegular />}
                        onClick={() => mergeMutation.mutate(apptReturn.id)}
                        disabled={mergeMutation.isPending}
                      >
                        {t('workflowTab.duplicateWarning.mergeIntoThis')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
